import { useState, useCallback, useMemo } from 'react'
import { Header } from './components/Header'
import { Footer } from './components/Footer'
import { ContentSection } from './components/ContentSection'
import { useI18n } from './i18n/index.tsx'
import { trackEvent } from '@/lib/analytics'
import {
  Type,
  Copy,
  Check,
  Trash2,
  FileText,
  Clock,
  AlignLeft,
  Hash,
  BarChart3
} from 'lucide-react'

// Common English stop words
const EN_STOP_WORDS = new Set([
  'a','an','the','and','or','but','in','on','at','to','for','of','with','by','is','are','was','were','be','been','being','have','has','had','do','does','did','will','would','could','should','may','might','must','shall','can','need','dare','ought','used','it','its','this','that','these','those','i','me','my','myself','we','our','ours','ourselves','you','your','yours','yourself','yourselves','he','him','his','himself','she','her','hers','herself','they','them','their','theirs','themselves','what','which','who','whom','whose','when','where','why','how','all','each','few','more','most','other','some','such','no','nor','not','only','own','same','so','than','too','very','just','now','then','also','here','there','up','out','if','about','into','through','during','before','after','above','below','between','under','again','further','once','off','over','down']
)

// Common Chinese stop words
const ZH_STOP_WORDS = new Set([
  '的','了','在','是','我','有','和','就','不','人','都','一','一个','上','也','很','到','说','要','去','你','会','着','没有','看','好','自己','这','那','有','与','及','等','对','可以','为','或','但','而','因为','所以','如果','虽然','但是','然后','之前','之后','现在','已经','正在','将要','就是','即','才','都','全','把','被','让','给','从','向','往','于','由','自','至','及','等','或','和','与','同','跟','比','较','更','最','太','很','非常','十分','特别','比较','相对','绝对','确实','的确','真的','假的','可能','也许','大概','似乎','好像','其实','实际上','事实上','本来','原来','当然','自然','当然','竟然','居然','果然','当然']
)

interface Stats {
  characters: number
  charactersNoSpaces: number
  words: number
  sentences: number
  paragraphs: number
  lines: number
  readingTimeMin: number
  readingTimeSec: number
  avgWordLength: number
  longestWord: string
}

function analyzeText(text: string): Stats {
  const characters = text.length
  const charactersNoSpaces = text.replace(/\s/g, '').length

  // Words: split by whitespace and punctuation
  const wordMatches = text.match(/[a-zA-Z0-9\u4e00-\u9fff]+/g) || []
  const words = wordMatches.length

  // Sentences: split by . ! ? 。 ！ ？ followed by space or end
  const sentenceMatches = text.split(/[.!?。！？]+\s*|[\n]+/).filter(s => s.trim().length > 0)
  const sentences = Math.max(1, sentenceMatches.length)

  // Paragraphs: split by double newline or more
  const paragraphs = text.split(/\n{2,}/).filter(p => p.trim().length > 0).length || (text.trim() ? 1 : 0)

  // Lines: split by single newline
  const lines = text.split('\n').length || (text.trim() ? 1 : 0)

  // Reading time: English ~200 wpm, Chinese ~400 chars/min
  const readingTimeMin = Math.ceil(words / 200)
  const readingTimeSec = Math.ceil(words / 200 * 60)

  // Avg word length
  const wordLengths = wordMatches.map(w => w.length)
  const avgWordLength = words > 0 ? Math.round(wordLengths.reduce((a, b) => a + b, 0) / words) : 0

  // Longest word
  const longestWord = wordMatches.reduce((a, b) => a.length >= b.length ? a : b, '')

  return {
    characters,
    charactersNoSpaces,
    words,
    sentences,
    paragraphs,
    lines,
    readingTimeMin,
    readingTimeSec,
    avgWordLength,
    longestWord,
  }
}

function getTopWords(text: string, limit: number = 10): { word: string; count: number }[] {
  const matches = text.match(/[a-zA-Z0-9\u4e00-\u9fff]+/g) || []
  const counts: Record<string, number> = {}

  for (const word of matches) {
    const lower = word.toLowerCase()
    // Skip numbers and very short words
    if (/^\d+$/.test(lower) || lower.length <= 1) continue
    // Skip stop words (check both English and Chinese)
    if (EN_STOP_WORDS.has(lower) || ZH_STOP_WORDS.has(lower) || ZH_STOP_WORDS.has(word)) continue
    counts[lower] = (counts[lower] || 0) + 1
  }

  return Object.entries(counts)
    .map(([word, count]) => ({ word, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit)
}

function StatCard({ icon, label, value, sublabel }: {
  icon: React.ReactNode
  label: string
  value: string | number
  sublabel?: string
}) {
  return (
    <div className="bg-surface rounded-xl shadow-sm border border-border p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="text-brand-primary">{icon}</div>
        <span className="text-xs text-muted-foreground uppercase tracking-wide">{label}</span>
      </div>
      <div className="text-2xl md:text-3xl font-bold text-foreground">{value}</div>
      {sublabel && <div className="text-xs text-muted-foreground mt-1">{sublabel}</div>}
    </div>
  )
}

function App() {
  const { t } = useI18n()
  const [text, setText] = useState('')
  const [copied, setCopied] = useState(false)

  const stats = useMemo(() => analyzeText(text), [text])
  const topWords = useMemo(() => getTopWords(text), [text])

  const copyText = useCallback(async () => {
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      trackEvent('tool_action', { action: 'copy', tool: 'word-counter' })
    } catch {}
  }, [text])

  const clearText = useCallback(() => {
    setText('')
    trackEvent('tool_action', { action: 'clear', tool: 'word-counter' })
  }, [])

  return (
    <div className="min-h-screen bg-background flex flex-col transition-colors duration-200">
      <Header />

      {/* Hero */}
      <div className="text-center py-6 px-4">
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
          {t('wordCounter.title') || 'Word Counter'}
        </h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          {t('wordCounter.subtitle') || 'Count words, characters, sentences, paragraphs. Analyze keyword density. '}
          <span className="text-primary font-medium">{t('wordCounter.privacy') || '100% client-side'}</span>
          , {t('wordCounter.noUpload') || 'no upload needed'}.
        </p>
      </div>

      <main className="flex-1 w-full max-w-6xl mx-auto px-4 pb-8 space-y-4">
        {/* Input Area */}
        <div className="bg-surface rounded-xl shadow-sm border border-border p-4 md:p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Type className="w-4 h-4 text-brand-primary" />
              <span className="text-sm font-medium text-foreground">{t('wordCounter.inputLabel') || 'Paste or type your text'}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={copyText}
                disabled={!text}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border bg-background hover:bg-muted transition-colors disabled:opacity-50"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? (t('actions.copied') || 'Copied!') : (t('actions.copy') || 'Copy')}
              </button>
              <button
                onClick={clearText}
                disabled={!text}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border bg-background hover:bg-red-50 dark:hover:bg-red-950/30 hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50"
              >
                <Trash2 className="w-3.5 h-3.5" />
                {t('actions.clear') || 'Clear'}
              </button>
            </div>
          </div>

          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={t('wordCounter.placeholder') || 'Paste your text here to analyze...'}
            className="w-full h-64 px-4 py-3 bg-background border border-border rounded-lg text-foreground text-sm resize-y outline-none focus:ring-2 focus:ring-brand-primary/50 font-mono leading-relaxed"
            spellCheck={false}
          />
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard
            icon={<FileText className="w-4 h-4" />}
            label={t('wordCounter.words') || 'Words'}
            value={stats.words}
            sublabel={stats.words === 1 ? t('wordCounter.wordSingular') || 'word' : t('wordCounter.wordsPlural') || 'words'}
          />
          <StatCard
            icon={<Hash className="w-4 h-4" />}
            label={t('wordCounter.characters') || 'Characters'}
            value={stats.characters}
            sublabel={`${stats.charactersNoSpaces} ${t('wordCounter.noSpaces') || 'no spaces'}`}
          />
          <StatCard
            icon={<AlignLeft className="w-4 h-4" />}
            label={t('wordCounter.sentences') || 'Sentences'}
            value={stats.sentences}
          />
          <StatCard
            icon={<BarChart3 className="w-4 h-4" />}
            label={t('wordCounter.paragraphs') || 'Paragraphs'}
            value={stats.paragraphs}
          />
          <StatCard
            icon={<Clock className="w-4 h-4" />}
            label={t('wordCounter.readingTime') || 'Reading Time'}
            value={`${stats.readingTimeMin}m`}
            sublabel={`${stats.readingTimeSec}s`}
          />
          <StatCard
            icon={<Type className="w-4 h-4" />}
            label={t('wordCounter.avgWord') || 'Avg Word'}
            value={stats.avgWordLength}
            sublabel={stats.longestWord ? `${t('wordCounter.longest') || 'Longest'}: ${stats.longestWord.length}` : undefined}
          />
        </div>

        {/* Keyword Density */}
        {topWords.length > 0 && (
          <div className="bg-surface rounded-xl shadow-sm border border-border p-4 md:p-6">
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-brand-primary" />
              {t('wordCounter.keywordDensity') || 'Keyword Density'}
            </h3>
            <div className="space-y-3">
              {topWords.map(({ word, count }) => {
                const max = topWords[0].count
                const percent = Math.round((count / max) * 100)
                return (
                  <div key={word} className="flex items-center gap-3">
                    <span className="text-sm font-medium text-foreground w-24 truncate">{word}</span>
                    <div className="flex-1 h-6 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-brand-primary rounded-full transition-all duration-500"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                    <span className="text-sm text-muted-foreground w-12 text-right">{count}</span>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Empty state hint */}
        {!text && (
          <div className="text-center py-8">
            <p className="text-sm text-muted-foreground">
              {t('wordCounter.emptyHint') || 'Start typing or paste text above to see statistics'}
            </p>
          </div>
        )}

        {/* Privacy Note */}
        <div className="text-center">
          <p className="text-xs text-muted-foreground">
            {t('security.note')}
          </p>
        </div>
      </main>

      <ContentSection />


      <Footer />
    </div>
  )
}

export default App
